import { useState } from "react";
import {
  Video,
  X,
  ChevronDown,
  ChevronRight,
  Calendar,
  FileText,
  Play,
  BookOpen,
  RefreshCw,
  Loader,
  Trash2,
  Eye,
  FileSpreadsheet,
  AlertTriangle,
  Upload,
} from "lucide-react";
import VideoPlayer from "./VideoPlayer";
import axios from "axios";
import apiClient from "../utils/apiClient";
import { processTextFormatting, successMessage, getAuthToken } from "../utils/helpers";
import { NODE_BACKEND_URL } from "../utils/constant";
import { Pagination, ConfirmDialog, SimpleTooltip } from "./design-system";
import { useTheme } from "../utils/theme";
import VideoCsv from "./VideoCsv"; // Import the VideoCsv component
import { getSelectedSport } from "../utils/helpers";
import YouTubeErrorDisplay, { isYouTubeError, YouTubeRestrictionsTooltip } from "./YouTubeErrorDisplay";

const VideoAnalyticsTable = ({
  setExistingVideos,
  existingVideos = [],
  serIsShowCsv=false,
  totalRecords = 0,
  currentPage,
  handlePageChange,
  pageSize,
  is_player_screen = true,
  onRefresh,
  selectedFolderId,
  allFolders = [],
  isLoading = false,
  onShowCsvViewer = null,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { theme } = useTheme();
  // V2 OLED Design: Always use dark mode aesthetic
  const isDarkMode = true;

  // States for CSV viewer
  const [showCsvViewer, setShowCsvViewer] = useState(false);
  const [csvViewerData, setCsvViewerData] = useState(null);
  const [loadingCsvViewer, setLoadingCsvViewer] = useState(false);
  const [selectedCsvFile, setSelectedCsvFile] = useState(null);

  const toggleRowExpansion = (analysisId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(analysisId)) {
      newExpandedRows.delete(analysisId);
    } else {
      newExpandedRows.add(analysisId);
    }
    setExpandedRows(newExpandedRows);
  };

  const onDeleteAnalysis = async (id) => {
    await axios.post(`${NODE_BACKEND_URL}/api/delete-video-analysis`, {
      id: id,
    }, {
      headers: { Authorization: getAuthToken() }
    });
    setExistingVideos((prev) => prev?.filter((v) => v?.id !== id));
    successMessage("Succesfully deleted");
  };
  const handleDeleteAnalysis = (analysisId) => {
    if (onDeleteAnalysis) {
      onDeleteAnalysis(analysisId);
    }
  };

  // Fetch CSV analysis data
  const fetchCsvAnalysis = async (analysis) => {
    if (!analysis?.csv_id) {
      console.error('No CSV ID available for this analysis');
      return;
    }

    setLoadingCsvViewer(true);
    setSelectedCsvFile(analysis);

    try {
      const url = `https://slam-all-python-359065791766.us-central1.run.app/${getSelectedSport()}/video-analysis/plays/${analysis.csv_id}`;

      const res = await apiClient.get(url);
      const data = res?.data;

      if (data?.success) {
        console.log('🎥 CSV Analysis Data:', data?.data);
        setCsvViewerData(data?.data);
        
        // Notify parent component if callback exists
        if (typeof onShowCsvViewer === 'function') {
          onShowCsvViewer(true);
        }
        
        setShowCsvViewer(true);
        serIsShowCsv(true)
      } else {
        console.warn('⚠️ CSV API response error:', data?.message);
        successMessage('Failed to load CSV data', 'error');
      }
    } catch (error) {
      console.error('❌ CSV API call error:', error);
      successMessage('Error loading CSV data', 'error');
    } finally {
      setLoadingCsvViewer(false);
    }
  };

  // Handle going back from CSV viewer
  const handleBackFromCsvViewer = () => {
    setShowCsvViewer(false);
    setCsvViewerData(null);
    serIsShowCsv(false)
    setSelectedCsvFile(null);
    
    // Notify parent component if callback exists
    if (typeof onShowCsvViewer === 'function') {
      onShowCsvViewer(false);
    }
  };

  // Status colors now handled inline with V2 OLED badge styling

  // Check if the analysis has a YouTube-related failure
  const hasYouTubeFailure = (analysis) => {
    if (analysis?.status?.toLowerCase() !== 'failed') return false;
    // Check if it's a YouTube URL or has YouTube-related error
    const isYouTubeUrl = analysis?.gs_uri?.includes('youtube.com') || analysis?.gs_uri?.includes('youtu.be');
    const hasYouTubeErrorMsg = isYouTubeError(analysis?.response);
    return isYouTubeUrl || hasYouTubeErrorMsg;
  };

  // Get a short error summary for the table cell
  const getErrorSummary = (response) => {
    if (!response) return '-';
    // Extract the main error title from the response (usually in **bold**)
    const boldMatch = response.match(/\*\*([^*]+)\*\*/);
    if (boldMatch) {
      return boldMatch[1];
    }
    // Fallback to first 100 characters
    return response.length > 100 ? response.substring(0, 100) + '...' : response;
  };

  // If showing CSV viewer, render the VideoCsv component
  if (showCsvViewer && selectedCsvFile) {
    console.log('selectedCsvFile', selectedCsvFile)
    return (
      <VideoCsv
        playsData={csvViewerData}
        videoUrl={selectedCsvFile?.secure_url}
        onBack={handleBackFromCsvViewer}
      />
    );
  }

  return (
    <div className={`${!is_player_screen ? "px-4" : ""} pb-4`}>
      {/* V2 OLED Glassmorphism Container */}
      <div className="relative rounded-2xl overflow-hidden backdrop-blur-md bg-zinc-900/80 border border-white/10 shadow-2xl">
        {/* Header with gradient accent */}
        <div className="p-4 border-b border-white/5 bg-gradient-to-r from-amber-500/10 via-transparent to-transparent flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold text-amber-400">
              {window.location.search.includes('folder=') ?
                `Folder: ${allFolders.find(f => f.id === selectedFolderId)?.name || 'Selected Folder'}` :
                'Video Analytics History'}
            </h3>
            <p className="text-sm text-amber-500/70 mt-1">
              {totalRecords || 0} Analysis found
            </p>
          </div>
          <button
            onClick={() => {
              setIsRefreshing(true);
              onRefresh().finally(() => setIsRefreshing(false));
            }}
            className="flex items-center px-4 py-2 text-sm font-medium rounded-xl bg-zinc-800/80 hover:bg-zinc-700 border border-zinc-700/50 text-zinc-300 hover:text-white transition-all hover:scale-105"
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <Loader size={16} className="mr-2 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw size={16} className="mr-2" />
                Refresh
              </>
            )}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full bg-transparent">
            {/* V2 OLED Table Header */}
            <thead className="bg-zinc-800/50 border-b border-white/5">
              <tr>
                <th className="text-left p-4 font-medium text-xs uppercase tracking-wider text-zinc-400">
                  Title
                </th>
                <th className="text-left p-4 font-medium text-xs uppercase tracking-wider text-zinc-400">
                  Video Name
                </th>
                <th className="text-left p-4 font-medium text-xs uppercase tracking-wider text-zinc-400">
                  Status
                </th>
                <th className="text-left p-4 font-medium text-xs uppercase tracking-wider text-zinc-400">
                  Response
                </th>
                <th className="text-left p-4 font-medium text-xs uppercase tracking-wider text-zinc-400">
                  Date Created
                </th>
                <th className="text-left p-4 font-medium text-xs uppercase tracking-wider text-zinc-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center py-16">
                    <Loader size={36} className="mx-auto mb-4 text-amber-400 animate-spin" />
                    <p className="text-zinc-400">Loading video analysis...</p>
                  </td>
                </tr>
              ) : !existingVideos?.length ? (
                <tr>
                  <td colSpan="6" className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-zinc-800/50 flex items-center justify-center border border-white/5">
                      <Video className="w-8 h-8 text-zinc-600" />
                    </div>
                    <p className="text-zinc-400 font-medium">No video analysis found</p>
                    <p className="text-sm text-zinc-600 mt-1">Analyze a video to see it here</p>
                  </td>
                </tr>
              ) : (
                existingVideos?.map((analysis, index) => (
                  <>
                    {/* V2 OLED Table Row */}
                    <tr
                      key={analysis.id}
                      className="border-b border-white/5 hover:bg-zinc-800/50 transition-all cursor-pointer group"
                      onClick={() =>
                        analysis.response &&
                        analysis.response !== "Analysis in progress..." &&
                        analysis.response !== "-" &&
                        toggleRowExpansion(analysis.id)
                      }
                    >
                      <td className="p-4">
                        <div className="flex items-center">
                          {analysis.response &&
                            analysis.response !== "Analysis in progress..." &&
                            analysis.response !== "-" && (
                              <div className="mr-2">
                                {expandedRows.has(analysis.id) ? (
                                  <ChevronDown className="w-4 h-4 text-amber-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300" />
                                )}
                              </div>
                            )}
                          <div>
                            <div className="text-zinc-200 font-medium capitalize group-hover:text-white transition-colors">
                              {analysis?.title || "Untitled Analysis"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center">
                          <div>
                            <div className="capitalize text-zinc-400 group-hover:text-zinc-300 transition-colors">
                              {analysis?.video_name || "-"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span
                            className={`capitalize font-medium px-2 py-0.5 rounded-md text-xs ${
                              analysis?.status?.toLowerCase() === 'completed'
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : analysis?.status?.toLowerCase() === 'processing'
                                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                : analysis?.status?.toLowerCase() === 'failed'
                                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                : 'bg-zinc-700/50 text-zinc-400 border border-zinc-600/30'
                            }`}
                          >
                            {analysis?.status || "-"}
                          </span>
                          {hasYouTubeFailure(analysis) && (
                            <SimpleTooltip content="YouTube video processing failed - click to see details">
                              <AlertTriangle
                                size={16}
                                className="text-orange-400 cursor-help"
                              />
                            </SimpleTooltip>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-zinc-400 max-w-xs">
                          {analysis?.status?.toLowerCase() === 'failed' ? (
                            <div className="flex items-center gap-2">
                              <span className={`truncate ${hasYouTubeFailure(analysis) ? 'text-orange-400' : 'text-red-400'}`}>
                                {getErrorSummary(analysis?.response)}
                              </span>
                            </div>
                          ) : (
                            <span className="truncate block">{analysis?.response || "-"}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-zinc-300 font-mono text-sm">
                          {analysis?.created_at
                            ? new Date(analysis.created_at).toLocaleDateString()
                            : "-"}
                        </div>
                        <div className="text-xs text-zinc-500 font-mono">
                          {analysis?.created_at
                            ? new Date(analysis.created_at).toLocaleTimeString()
                            : ""}
                        </div>
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex space-x-2">
                          {/* Show CSV button only if csv_id exists */}
                          {analysis?.csv_id && (
                            <button
                              onClick={() => fetchCsvAnalysis(analysis)}
                              disabled={loadingCsvViewer && selectedCsvFile?.id === analysis.id}
                              className="flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 hover:border-amber-500/50 transition-all"
                            >
                              {loadingCsvViewer && selectedCsvFile?.id === analysis.id ? (
                                <Loader size={12} className="mr-1 animate-spin" />
                              ) : (
                                <FileSpreadsheet size={12} className="mr-1" />
                              )}
                              CSV Data
                            </button>
                          )}
                          <ConfirmDialog
                            title="Delete Video Analysis"
                            description="Are you sure you want to delete this video analysis?"
                            onConfirm={() => handleDeleteAnalysis(analysis.id)}
                            okText="Yes"
                            cancelText="No"
                            destructive
                          >
                            <button className="flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 hover:border-red-500/50 transition-all">
                              <Trash2 size={12} className="mr-1" />
                              Delete
                            </button>
                          </ConfirmDialog>
                        </div>
                      </td>
                    </tr>

                    {/* V2 OLED Expanded Row Content */}
                    {expandedRows?.has(analysis?.id) && (
                      <tr
                        key={`${analysis.id}-expanded`}
                        className="border-b border-white/5 bg-zinc-800/30"
                      >
                        <td colSpan="6" className="p-0">
                          <div className="p-6">
                            <div className="flex flex-col lg:flex-row gap-6">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                                {/* Video Player Section */}
                                <div className="md:col-span-1">
                                  <div className="rounded-xl p-4 h-full bg-zinc-800/50 border border-white/5">
                                    <div className="aspect-video mb-3 rounded-lg overflow-hidden bg-black">
                                      <VideoPlayer selectedVideo={analysis} />
                                    </div>
                                    <h3 className="font-medium text-white">
                                      {analysis.title}
                                    </h3>
                                    <p className="text-sm text-zinc-500 font-mono">
                                      {new Date(
                                        analysis.created_at
                                      ).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>

                                <div className="md:col-span-2">
                                  <div className="rounded-xl p-4 h-full bg-zinc-800/50 border border-white/5">
                                    <h3 className="text-xl font-semibold text-amber-400 mb-3">
                                      {analysis?.status?.toLowerCase() === 'failed' ? 'Error Details' : 'Analysis Results'}
                                    </h3>

                                    {/* Show YouTube Error Display for failed YouTube videos */}
                                    {hasYouTubeFailure(analysis) ? (
                                      <YouTubeErrorDisplay
                                        errorMessage={analysis.response}
                                        showUploadButton={true}
                                        onUploadClick={() => {
                                          // Navigate to video analytics upload page
                                          window.location.href = '/video-analytics';
                                        }}
                                      />
                                    ) : analysis?.status?.toLowerCase() === 'failed' ? (
                                      // Show generic error display for non-YouTube failures
                                      <div className="p-4 rounded-xl border bg-red-500/10 border-red-500/30">
                                        <div className="flex items-start gap-3">
                                          <AlertTriangle className="w-6 h-6 flex-shrink-0 text-red-400" />
                                          <div>
                                            <h4 className="font-semibold text-red-300">
                                              Analysis Failed
                                            </h4>
                                            <p className="mt-2 text-sm text-zinc-400">
                                              {analysis.response}
                                            </p>
                                            <div className="mt-4">
                                              <button
                                                onClick={() => window.location.href = '/video-analytics'}
                                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 text-black hover:bg-amber-400 transition-colors"
                                              >
                                                <Upload size={16} />
                                                Try Again
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="prose prose-invert max-w-none">
                                        <div className="markdown-content analysis-text overflow-auto max-h-[60vh] py-2 text-zinc-300">
                                          {analysis.response ? (
                                            analysis.response
                                              .split("\n\n")
                                              .map((paragraph, i) => (
                                                <p
                                                  key={i}
                                                  className="my-4 leading-relaxed"
                                                >
                                                  {processTextFormatting(
                                                    paragraph
                                                  )}
                                                </p>
                                              ))
                                          ) : (
                                            <div className="text-center py-20">
                                              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-zinc-800/50 flex items-center justify-center border border-white/5">
                                                <BookOpen
                                                  size={24}
                                                  className="text-zinc-600"
                                                />
                                              </div>
                                              <p className="text-zinc-500">
                                                No analysis available for this
                                                video
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* V2 OLED Pagination */}
        {existingVideos?.length ? (
          <div className="py-4 px-4 border-t border-white/5 flex justify-center bg-zinc-800/30">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              onChange={handlePageChange}
              showSizeChanger
              pageSizeOptions={[5, 10, 20, 50]}
              isDarkMode={true}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default VideoAnalyticsTable;
